import React, { useEffect, useRef, useState } from "react";
import { RiCheckboxCircleFill, RiErrorWarningFill } from "react-icons/ri";
import { Modal } from "react-bootstrap";
import emailjs from "@emailjs/browser";
import { setScrollLocked } from "../../lib/motion";
import "./getinvolvedmodal.css";

const CONTACT_EMAIL = "salesbarishal1988@gmail.com";

const FIELDS = [
  {
    name: "name",
    label: "Name",
    placeholder: "Your full name",
    autoComplete: "name",
    missing: "Tell us your name.",
  },
  {
    name: "phone",
    label: "Phone",
    type: "tel",
    placeholder: "01XXXXXXXXX",
    autoComplete: "tel",
    inputMode: "tel",
    missing: "We need a number to reach you on.",
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    placeholder: "you@example.com",
    autoComplete: "email",
    missing: "We reply to this address.",
  },
  {
    name: "address",
    label: "Where you live",
    placeholder: "Area, city",
    autoComplete: "street-address",
    missing: "Tell us roughly where you are based.",
  },
];

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const EMPTY = { name: "", phone: "", email: "", address: "", reason: "" };

const GetInvolvedModal = ({ show, onHide }) => {
  useEffect(() => {
    setScrollLocked(show);
    return () => setScrollLocked(false);
  }, [show]);

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: null, message: "" });
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const update = (name) => (event) => {
    setForm((prev) => ({ ...prev, [name]: event.target.value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setStatus({ type: null, message: "" });
  };

  /* Same rule for blur and for submit, so the two can never disagree — and
     the same behaviour as the donation form, which validates on blur too. */
  const checkField = (name, value) => {
    const field = FIELDS.find((f) => f.name === name);
    if (!value.trim()) {
      return field ? field.missing : "A sentence is enough — it helps us place you well.";
    }
    if (name === "email" && !isEmail(value)) {
      return "Check the email address — it is missing something.";
    }
    return null;
  };

  const validateOnBlur = (name) => (event) => {
    setErrors((prev) => ({
      ...prev,
      [name]: checkField(name, event.target.value) || undefined,
    }));
  };

  const close = () => {
    setStatus({ type: null, message: "" });
    setErrors({});
    onHide();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const found = {};
    FIELDS.forEach((field) => {
      if (!form[field.name].trim()) found[field.name] = field.missing;
    });
    if (form.email.trim() && !isEmail(form.email)) {
      found.email = "Check the email address — it is missing something.";
    }
    if (!form.reason.trim()) {
      found.reason = "A sentence is enough — it helps us place you well.";
    }

    setErrors(found);
    if (Object.keys(found).length) {
      setStatus({ type: "error", message: "Check the highlighted fields and try again." });
      return;
    }

    const elements = formRef.current.elements;
    elements.title.value = "Get Involved Request";
    elements.time.value = new Date().toLocaleString();

    setLoading(true);
    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_INVOLVEMENT_TEMPLATE_ID,
        formRef.current,
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      setStatus({
        type: "success",
        message: "Thank you. We have your details and will be in touch.",
      });
      setForm(EMPTY);
      window.setTimeout(close, 1800);
    } catch {
      setStatus({
        type: "error",
        message: `We could not send that. Email us at ${CONTACT_EMAIL} instead.`,
      });
    }
    setLoading(false);
  };

  return (
    <Modal show={show} onHide={close} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Get involved</Modal.Title>
          <p className="involve__sub">
            Volunteers, physiotherapists, teachers and members are all welcome.
          </p>
        </div>
      </Modal.Header>

      <Modal.Body>
        <form ref={formRef} onSubmit={handleSubmit} noValidate className="involve__form">
          <input type="hidden" name="title" />
          <input type="hidden" name="time" />

          <div className="involve__grid">
            {FIELDS.map((field) => {
              const id = `involve-${field.name}`;
              const invalid = Boolean(errors[field.name]);
              return (
                <div className="field" key={field.name}>
                  <label className="field__label" htmlFor={id}>
                    {field.label} <span className="field__req">*</span>
                  </label>
                  <input
                    id={id}
                    name={field.name}
                    type={field.type || "text"}
                    inputMode={field.inputMode}
                    autoComplete={field.autoComplete}
                    className="input-sales"
                    placeholder={field.placeholder}
                    value={form[field.name]}
                    onChange={update(field.name)}
                    onBlur={validateOnBlur(field.name)}
                    aria-invalid={invalid}
                    aria-describedby={invalid ? `${id}-error` : undefined}
                    autoFocus={field.name === "name"}
                  />
                  {invalid ? (
                    <p className="field__error" id={`${id}-error`}>
                      {errors[field.name]}
                    </p>
                  ) : null}
                </div>
              );
            })}

            <div className="field involve__grid-full">
              <label className="field__label" htmlFor="involve-reason">
                How would you like to help? <span className="field__req">*</span>
              </label>
              <textarea
                id="involve-reason"
                name="reason"
                rows={3}
                className="input-sales"
                placeholder="Skills, availability, or what drew you to this work"
                value={form.reason}
                onChange={update("reason")}
                aria-invalid={Boolean(errors.reason)}
                aria-describedby={errors.reason ? "involve-reason-error" : undefined}
              />
              {errors.reason ? (
                <p className="field__error" id="involve-reason-error">
                  {errors.reason}
                </p>
              ) : null}
            </div>
          </div>

          <div aria-live="polite">
            {status.type ? (
              <p className={`notice notice--${status.type} involve__status`}>
                {status.type === "success" ? (
                  <RiCheckboxCircleFill size={18} aria-hidden="true" />
                ) : (
                  <RiErrorWarningFill size={18} aria-hidden="true" />
                )}
                {status.message}
              </p>
            ) : null}
          </div>

          <div className="involve__actions">
            <button
              type="button"
              className="btn-sales btn-sales--ghost"
              onClick={close}
            >
              Cancel
            </button>
            <button type="submit" className="btn-sales" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                "Send my details"
              )}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default GetInvolvedModal;
